import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { BsArrowLeft } from 'react-icons/bs';
import styled from 'styled-components';
import withModalWrapper from '@containers/common/WithModalWrapper';

// selectors

// components
import SideNavContainer from '@components/common/nav/SideNavContainer';

// ActionCreators

// type
import HouseholdMembersTable from './HouseholdMembersTable';
import useHouseholdInfo from './useHouseholdInfo';
import EligibilityFactors from './EligibilityFactors';
import { getEligibilityFactorTestIdDisambiguators } from './eligibilityFactorTestIds';

// utils
import { calculateHouseholdNumber } from './utils/utility';

//Const

import { configs } from '@constants/configs/configs';

import MedicaidChipMembersTable from './MedicaidChipTable';
import { ScreenType } from './types/apiResponse';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { monthlySlcspPremiumModalConfig } from './modalConfig';

interface IHouseholds {
    householdSize?: number;
    householdIncome?: any;
    aptcEligibilityStartDate?: string;
    aptcEligibilityEndDate?: string;
    householdNumber?: number;
    aptcDetails?: {};
    members?: [Imembers];
}
interface Imembers {
    eligibilityFactors?: [];
}

const StyledMainContainer = styled.div`
    #main-content {
        padding: 1em 2em;
    }
`;

const StyledContainer = styled.div`
    border-radius: 5px;
    border: ${({ theme }) => `1px solid ${theme.gray8}`};
`;

const StyledEligibilityHeading = styled.div`
    justify-content: 'space-between';
`;

function ApplicationEligibility() {
    const encHouseHoldId = sessionStorage.getItem('encHouseHoldId');
    const { data, fetchApplicationEligibilityData } = useHouseholdInfo();
    //set member data
    const [memberData, setMemberData] = useState<[]>([]);
    const [households, sethouseholds] = useState<[IHouseholds]>();
    const [eligibilityFactors, seteligibilityFactors] = useState<[]>([]);
    const [selectedRow, setSelectedRow] = useState<string | number>('');
    const [selectedRowIndex, setselectedRowIndex] = useState<number>(0);
    const isEligibilityDetailOverriden = !!data?.houseHoldInfo?.overridden;
    // State Subsidy configuration returned by the eligibility response drives the
    // State Subsidy details shown on the worksheet (HIX-233726).
    const stateSubsidyModel = data?.houseHoldInfo?.stateSubsidyModel;
    const stateSubsidyApplicable = !!data?.houseHoldInfo?.stateSubsidyApplicable;
    
    // setting initial data on load
    useEffect(() => {
        if (data?.houseHoldInfo?.households?.[0]?.members) {
            const rawHouseholdNumber = data?.houseHoldInfo.households?.[0]?.householdNumber;
            selectHousehold(0);
            setselectedRowIndex(0);
            handleShowEligibilityFactor(0);
            setSelectedRow(calculateHouseholdNumber(rawHouseholdNumber));
            setMemberData(data?.houseHoldInfo.households?.[0]?.members || []);
        }
    }, [data]);

    useEffect(() => {
        const sessionData = localStorage.getItem('copySessionData');
        if (!sessionData) {
            return;
        }
        try {
            const applicationObject = JSON.parse(sessionData);
            fetchApplicationEligibilityData(applicationObject?.caseNumber);
        } catch (error) {
            console.debug('ApplicationEligibility: ignoring invalid copySessionData payload', error);
        }
    }, []);

    useEffect(() => {
        handleShowMember(selectedRowIndex);
    }, [selectedRowIndex, memberData]);

    const selectHousehold = selectedItem => {
        if (data?.houseHoldInfo?.households) {
            const selectedHouseHold = data?.houseHoldInfo?.households?.[selectedItem];
            const houseHolds = data?.houseHoldInfo?.households;
            houseHolds.forEach(elm => {
                delete elm?.isSelected;
            });
            if (selectedHouseHold) {
                Object.assign(selectedHouseHold, { isSelected: true });
            }
            houseHolds[selectedItem] = selectedHouseHold || [];
            sethouseholds(houseHolds);
        }
    };

    const selectedMemberId = (selectedItem: number) => {
        if (data?.houseHoldInfo?.households?.[selectedRowIndex]?.members) {
            const selectedMemeber = data?.houseHoldInfo?.households?.[selectedRowIndex]?.members?.[selectedItem];
            const members: any = data?.houseHoldInfo?.households?.[selectedRowIndex]?.members || [];
            members.forEach((elm: { isSelected }) => {
                delete elm?.isSelected;
            });
            if (selectedMemeber) {
                Object.assign(selectedMemeber, { isSelected: true });
            }
            members[selectedItem] = selectedMemeber || [];
            setMemberData(members);
        }
    };

    const handleShowMember = (index: any) => {
        const rawHouseholdNumber = data?.houseHoldInfo.households?.[index]?.householdNumber;
        selectHousehold(index);
        setselectedRowIndex(index);
        setSelectedRow(calculateHouseholdNumber(rawHouseholdNumber));
        setMemberData(data?.houseHoldInfo.households?.[index]?.members);
        handleShowEligibilityFactor(0);
    };

    const handleShowEligibilityFactor = (index: any) => {
        selectedMemberId(index);
        seteligibilityFactors(
            data?.houseHoldInfo.households?.[selectedRowIndex]?.members?.[index]?.eligibilityFactors || [],
        );
    };

    const eligibilityFactorTestIdDisambiguators = useMemo(
        () => getEligibilityFactorTestIdDisambiguators(eligibilityFactors as string[]),
        [eligibilityFactors],
    );

    return (
        <SideNavContainer>
            <StyledMainContainer className="desktop:grid-col-9 eligContent">
                <StyledContainer id="main-content">
                    <div className="back_to_application_from_eligibility">
                        <Link
                            data-testid="application-eligibility-back-to-application-link"
                            to={`${configs.routes.MEMBER_APP_LIST}/${encHouseHoldId}`}
                        >
                            <BsArrowLeft />
                            <FormattedMessage id="common.backToApplication" />
                        </Link>
                    </div>
                    <div className="grid-col-12">
                        <StyledEligibilityHeading className="grid-row --header">
                            <h3 data-testid="application-eligibility-details-heading">
                                <FormattedMessage id="application.eligibility.Details.heading" />
                            </h3>
                        </StyledEligibilityHeading>
                    </div>
                    <ConditionalRendering isTrue={isEligibilityDetailOverriden}>    
                        <AlertMessage content={<FormattedMessage id="application.eligibility.overriden.alert" />} type={'warning'} showIcon={true} icon='exclamation-triangle' />
                    </ConditionalRendering>
                    <div>
                        <p data-testid="application-eligibility-run-date">
                            <FormattedMessage id="application.eligibility.run.date" />{' '}
                            {data?.houseHoldInfo?.households?.[0]?.aptcEligibilityStartDate}
                            <br />
                            <FormattedMessage id="application.eligibility.evaulated_as_of" />{' '}
                            {data?.houseHoldInfo?.households?.[0]?.aptcEligibilityEndDate}
                        </p>
                    </div>
                    <div>
                        {data?.houseHoldInfo?.eligibilityDetails?.length > 0 && (
                            <>
                                <h4 data-testid="application-eligibility-medicaid-chip-section-title">
                                    <FormattedMessage id="application.eligibility.medicaid.chip" />
                                </h4>
                                <div className="eligibility_Chip_table" data-testid="application-eligibility-medicaid-chip-table">
                                    <MedicaidChipMembersTable
                                        data={data?.houseHoldInfo?.eligibilityDetails || []}
                                        screenType={ScreenType.MedicaidMember}
                                        handleShowMember={handleShowEligibilityFactor}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <div>
                        <h4 data-testid="application-eligibility-tax-household-section-title">
                            <FormattedMessage id="application.eligibility.tax.household" />
                        </h4>
                        <span data-testid="application-eligibility-selected-household-number">
                            <FormattedMessage id="application.eligibility.selected.household.number" />
                            {`(${selectedRow})`}
                        </span>
                        <div className="eligibility_houshold_table" data-testid="application-eligibility-household-table">
                            <HouseholdMembersTable
                                data={households}
                                screenType={ScreenType.Household}
                                handleShowMember={handleShowMember}
                                stateSubsidyModel={stateSubsidyModel}
                                stateSubsidyApplicable={stateSubsidyApplicable}
                            />
                        </div>
                        <h4 data-testid="application-eligibility-household-members-section-title">
                            <FormattedMessage id="application.eligibility.household.members" />
                        </h4>
                        <div className="eligibility_Members_table" data-testid="application-eligibility-household-members-table">
                            <HouseholdMembersTable
                                data={memberData}
                                screenType={ScreenType.Member}
                                handleShowMember={handleShowEligibilityFactor}
                                stateSubsidyModel={stateSubsidyModel}
                                stateSubsidyApplicable={stateSubsidyApplicable}
                            />
                        </div>
                    </div>
                    <div className=""></div>
                    {eligibilityFactors.length > 0 && (
                        <>
                            <h4 data-testid="application-eligibility-factors-section-title">
                                <FormattedMessage id="application.eligibility.factors" />
                            </h4>
                            <div className="eligibility_factors_container" data-testid="application-eligibility-factors-container">
                                <ul data-testid="application-eligibility-factors-list">
                                    {eligibilityFactors.map((factors: string, index: number) => (
                                        <EligibilityFactors
                                            key={`${factors}-${index}`}
                                            factors={factors}
                                            testIdDisambiguator={eligibilityFactorTestIdDisambiguators[index]}
                                        />
                                    ))}
                                </ul>
                            </div>
                        </>
                    )}
                </StyledContainer>
            </StyledMainContainer>
        </SideNavContainer>
    );
}

export default withModalWrapper(ApplicationEligibility, monthlySlcspPremiumModalConfig);
